import { useState } from "react";
import { Link } from "react-router-dom";
import { RotateCcw, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { useLoad, act } from "@/lib/hooks";
import { api, fmt, fdate } from "@/lib/api";
import { useAccess } from "@/context/AuthContext";
import { PageHeader, Section, StatusBadge, EmptyRow } from "@/components/common";
import { PageSearch } from "@/components/PageSearch";
import { RaiseTicketButton } from "@/components/agyin/RaiseTicketButton";

function RowStatus({ r }) {
  if (r.kind === "reversal" || r.kind === "pos_reversal") return <StatusBadge status="reversed" label="reversal" />;
  if (r.reversed || (r.reversed_amount > 0 && r.reversed_amount >= r.amount - 0.004)) return <StatusBadge status="reversed" label="reversed" />;
  if (r.source === "pos") return <StatusBadge testId={`receipt-status-${r.number}`} status={r.balance_due > 0.004 ? "part_paid" : "paid"} label={r.balance_due > 0.004 ? "part paid" : "paid in full"} />;
  return <StatusBadge status="paid" label="received" />;
}

function DocCell({ r }) {
  if (r.source !== "pos") return <Link className="font-medium text-[#13294B] hover:underline" to={`/app/invoices/${r.invoice_id}`}>{r.invoice_number}</Link>;
  return <span className="text-xs">Sale {r.sale_number}{r.job_id && <> · <Link className="font-medium text-[#13294B] hover:underline" to={`/app/jobs/${r.job_id}`}>{r.job_number}</Link></>}
    <span className="block text-slate-500" data-testid={`receipt-sale-summary-${r.number}`}>Total {fmt(r.sale_total, r.currency)} · Paid {fmt(r.sale_paid, r.currency)} · Balance {fmt(r.balance_due, r.currency)}</span></span>;
}

export default function Payments() {
  const [rows, reload] = useLoad("/tenant/receipts");
  const [rev, setRev] = useState(null);
  const [reason, setReason] = useState("");
  const { canWrite } = useAccess();
  const can = canWrite(["tenant_admin", "accountant"]);
  const doReverse = async () => {
    if (await act(() => api.post(`/tenant/receipts/${rev.id}/reverse`, { reason }), "Receipt reversed")) { setRev(null); setReason(""); reload(); }
  };
  return (
    <>
      <PageHeader title="Payments & receipts" subtitle="Payments your team recorded against invoices and at the counter (cash, bank transfer, cheque, mobile money).">
        <PageSearch testId="payments-search" placeholder="Search receipt, invoice or customer" />
        <Button variant="outline" data-testid="payments-refresh-button" onClick={reload}><RefreshCw className="mr-1 h-4 w-4" />Refresh</Button>
      </PageHeader>
      <Section>
        <div className="overflow-x-auto"><table className="po-table" data-testid="receipts-table">
          <thead><tr><th>Receipt</th><th>Date</th><th>Invoice / sale</th><th>Customer</th><th>Method</th><th>Reference</th><th>Recorded by</th><th>Status</th><th className="po-num">Amount</th><th></th></tr></thead>
          <tbody>{rows?.length === 0 && <EmptyRow cols={10} text="No payments recorded yet." />}{rows?.map((r) => (
            <tr key={r.id} data-testid={`receipt-${r.number}`}>
              <td className="font-mono text-xs">{r.number}</td><td>{fdate(r.date || r.created_at)}</td>
              <td><DocCell r={r} /></td>
              <td>{r.customer_name || "—"}</td><td>{r.method_label || r.method}</td><td className="text-xs">{r.reference || "—"}</td><td>{r.recorded_by || "—"}</td>
              <td><RowStatus r={r} /></td>
              <td className="po-num" data-testid={`receipt-amount-${r.number}`}>{fmt(r.amount, r.currency)}{r.reversed_amount > 0 && <span className="block text-xs text-rose-700">{fmt(r.reversed_amount, r.currency)} reversed</span>}</td>
              <td className="text-right whitespace-nowrap"><RaiseTicketButton recordType="payment" recordId={r.id} label={r.number} size="sm" /> {r.kind === "payment" && !r.reversed && can && <Button size="sm" variant="outline" data-testid={`receipt-reverse-${r.number}`} onClick={() => setRev(r)}><RotateCcw className="mr-1 h-3.5 w-3.5" />Reverse</Button>}</td>
            </tr>))}</tbody>
        </table></div>
      </Section>
      <Dialog open={!!rev} onOpenChange={() => setRev(null)}>
        <DialogContent data-testid="reverse-dialog">
          <DialogHeader><DialogTitle>Reverse {rev?.number} ({rev && fmt(rev.amount, rev.currency)})</DialogTitle>
            <DialogDescription>Creates a reversing receipt and journal entry (Dr Accounts Receivable, Cr payment-method account) and restores the invoice balance.</DialogDescription></DialogHeader>
          <Textarea data-testid="reverse-reason-input" placeholder="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} />
          <DialogFooter><Button variant="destructive" data-testid="reverse-confirm-button" disabled={reason.trim().length < 3} onClick={doReverse}>Reverse receipt</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
